import React from "react";
import startCase from "lodash/startCase";
import {
    faFire,
    faInfoCircle,
    faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { Box, Flex } from "@singlestore/fusion/components/layout";
import { Paragraph } from "@singlestore/fusion/components/typography";
import { cva } from "class-variance-authority";

import { FaIcon } from "components/FaIcon/FaIcon";

import "./Admonition.scss";

type AdmonitionType =
    | "note"
    | "tip"
    | "notice"
    | "important"
    | "caution"
    | "warning"
    | "danger";

type AdmonitionStyle = "note" | "caution" | "danger";

const typeToStyle: Record<AdmonitionType, AdmonitionStyle> = {
    note: "note",
    tip: "note",
    notice: "note",
    important: "note",
    caution: "caution",
    warning: "caution",
    danger: "danger",
};

const admonition = cva("admonition", {
    variants: {
        style: {
            note: "note",
            caution: "caution",
            danger: "danger",
        },
    },
});

export type AdmonitionProps = {
    type?: AdmonitionType;
    title?: string;
    className?: string;
};

export function Admonition({
    type = "note",
    title: _title,
    className,
    children,
}: React.PropsWithChildren<AdmonitionProps>) {
    let icon;
    const style = typeToStyle[type];

    if (style === "note") {
        icon = <FaIcon icon={faInfoCircle} />;
    } else if (style === "caution") {
        icon = <FaIcon icon={faTriangleExclamation} />;
    } else if (style === "danger") {
        icon = <FaIcon icon={faFire} />;
    }

    let title = startCase(type);
    if (_title) {
        title = _title;
    }

    const classes = admonition({ className, style });

    return (
        <Flex className={classes} p="3x">
            <Box
                className="admonition-icon"
                mr="1-5x"
                flexGrow="0"
                flexShrink="0"
            >
                {icon}
            </Box>
            <Box className="admonition-content" flexGrow="1">
                <Paragraph
                    variant="body-3"
                    color="high-contrast"
                    fontWeight="bold"
                    className="admonition-title"
                >
                    {title}
                </Paragraph>
                <div className="admonition-body-container">{children}</div>
            </Box>
        </Flex>
    );
}
